import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { ErrorNote, useMe } from '../App.js';
import { api } from '../api.js';
import { useI18n } from '../i18n.js';
import { Icon } from '../ui.js';
import { Dialog } from './org/widgets.js';
import { EmptyState, formatKennitala } from './orgParts.js';

/**
 * Organizations (owner 2026-11-01: a top-level item instead of chips under the projects). Superadmins see every
 * organization and create new ones; others see their own.
 */
export function OrgsPage() {
  const { t } = useI18n();
  const me = useMe();
  const navigate = useNavigate();
  const superadmin = Boolean(me.data?.user.isSuperadmin);
  const orgs = useQuery({ queryKey: ['orgs'], queryFn: () => api.orgs() });
  const projects = useQuery({ queryKey: ['projects'], queryFn: () => api.projects() });
  const [creating, setCreating] = useState(false);
  const count = (slug: string) => (projects.data ?? []).filter((p) => p.org === slug).length;
  const list = [...(orgs.data ?? [])].sort((a, b) => a.name.localeCompare(b.name, 'is'));

  return (
    <div className="page">
      <header className="home-head">
        <h1 className="page-title">
          {t('organizations')}
          {list.length > 0 && <span className="title-count">{list.length}</span>}
        </h1>
        {superadmin && (
          <button className="primary" onClick={() => setCreating(true)}>
            <Icon.Plus />
            {t('newOrg')}
          </button>
        )}
      </header>
      <ErrorNote error={orgs.error} />
      {orgs.isPending && <p className="muted">{t('loading')}</p>}
      {orgs.data && list.length === 0 && <EmptyState text={t('noOrgs')} />}
      {list.length > 0 && (
        <div className="sheet">
          <table className="stack-sm register-table">
            <thead>
              <tr>
                <th>{t('organization')}</th>
                <th className="hide-sm">{t('address')}</th>
                <th className="num">{t('projects')}</th>
              </tr>
            </thead>
            <tbody>
              {list.map((o) => (
                <tr key={o.id} className="link-row" onClick={() => navigate(`/orgs/${o.slug}`)}>
                  <td className="cell-grow">
                    <span className="proj">
                      <span className="thumb letter" aria-hidden="true">
                        {o.name.slice(0, 1).toUpperCase()}
                      </span>
                      <span className="proj-text">
                        <Link
                          to={`/orgs/${o.slug}`}
                          className="cell-strong row-link"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {o.name}
                        </Link>
                        <span className="sub">
                          {o.kennitala ? `${t('kt')} ${formatKennitala(o.kennitala)}` : o.slug}
                        </span>
                      </span>
                    </span>
                  </td>
                  <td className="hide-sm">{o.address || <span className="muted">—</span>}</td>
                  <td className="num">{projects.data ? count(o.slug) : ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {creating && <NewOrgDialog onClose={() => setCreating(false)} />}
    </div>
  );
}

function NewOrgDialog({ onClose }: { onClose: () => void }) {
  const { t } = useI18n();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: '', slug: '', ownerEmail: '' });
  const create = useMutation({
    mutationFn: () =>
      api.createOrg({
        name: form.name,
        slug: form.slug,
        ...(form.ownerEmail ? { ownerEmail: form.ownerEmail } : {}),
      }),
    onSuccess: (o) => {
      void qc.invalidateQueries({ queryKey: ['orgs'] });
      navigate(`/orgs/${o.slug}`);
    },
  });
  return (
    <Dialog
      title={t('newOrg')}
      onClose={onClose}
      onSubmit={() => create.mutate()}
      footer={
        <>
          <button type="button" className="quiet" onClick={onClose}>
            {t('cancel')}
          </button>
          <button className="primary" disabled={create.isPending}>
            {t('create')}
          </button>
        </>
      }
    >
      <div className="form-grid two">
        <label className="span-all">
          {t('name')}
          <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        </label>
        <label>
          {t('slug')}
          <input
            required
            pattern="[a-z0-9][a-z0-9-]*[a-z0-9]"
            value={form.slug}
            onChange={(e) => setForm({ ...form, slug: e.target.value })}
          />
        </label>
        <label>
          {t('ownerEmail')}
          <input
            type="email"
            value={form.ownerEmail}
            onChange={(e) => setForm({ ...form, ownerEmail: e.target.value })}
          />
        </label>
      </div>
      <ErrorNote error={create.error} />
    </Dialog>
  );
}
